import { ActionCreator, Middleware, Reducer, Store, StoreEnhancer, applyMiddleware, createStore } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import { createLogger } from "redux-logger";
import { Persistor, persistReducer, persistStore } from "redux-persist";
import thunk from "redux-thunk";
import { SdkThunkDispatch } from "./types";
import { SdkPersistConfig, getPersistConfig } from "./persistConfig";
import { promise } from "./promise";

export interface SdkStore<S> extends Store<S> {
  dispatch: SdkThunkDispatch<S>;
}

export interface ConfigureStoreType<S> {
  store: SdkStore<S>;
  persistor?: Persistor;
}

export interface SdkConfigureStore {
  appReducers: Reducer;
  appMiddlewares?: Middleware[];
  appEnhancers?: StoreEnhancer[];
  composeActionCreators?: { [key: string]: ActionCreator<any> };
  appPersistConfig?: SdkPersistConfig;
  onRehydrated?: () => void;
}

export function configureStore<S>({
  appReducers,
  appMiddlewares = [],
  appEnhancers = [],
  composeActionCreators = {},
  appPersistConfig,
  onRehydrated = (): void => undefined,
}: SdkConfigureStore): ConfigureStoreType<S> {
  const middleware: Middleware[] = [thunk, promise, ...appMiddlewares];
  const enhancers = [...appEnhancers];

  if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "test") {
    const logger = createLogger({
      collapsed: true,
      level: "info",
    });
    // @ts-expect-error wrong createLogger return type
    middleware.push(logger);
  }

  const composeEnhancers = composeWithDevTools({ actionCreators: composeActionCreators });

  enhancers.push(applyMiddleware(...middleware));
  const enhancer: StoreEnhancer = composeEnhancers(...enhancers);

  // const rootReducer = createRootReducer<S>(appReducers);
  let reducerToUse = appReducers;

  if (appPersistConfig) {
    const persistConfig = getPersistConfig(appPersistConfig);
    const persistedReducer = persistReducer(persistConfig, appReducers);
    reducerToUse = persistedReducer;
  }

  const store = createStore(reducerToUse, enhancer);

  let persistor;

  if (appPersistConfig) {
    persistor = persistStore(store, undefined, onRehydrated);
  }

  return { persistor, store };
}

export { SdkPersistConfig };
